// src/pages/PersonalStatementRequestPage.jsx
//
// ISOLATED Personal Statement request flow (/personal-statement/request).
// Auth-gated. Deliberately does NOT import or modify BookingPage,
// MyTutorReviewPanel, MyBookingsPanel or AccountPage -- it reuses only the
// /serviceRequests BACKEND, writing the same doc shape those forms write:
//
//   review       -> { type:'tutor_review', uid, status:'requested',
//                     payload:{ submissionText, serviceCategory:'personal_statement', ...applicant },
//                     createdAt: serverTimestamp() }
//   consultation -> { type:'booking', uid, status:'requested',
//                     payload:{ preferredDate, preferredTime, topic, serviceCategory:'personal_statement', ...applicant },
//                     createdAt: serverTimestamp() }
//
// firestore.rules (serviceRequests create) allowlists the 5 TOP-LEVEL keys and
// accepts `payload` as a generic map of <= 30 keys, bounding only
// tutor_review's submissionText (<= 50000). The applicant fields therefore need
// NO rules change. Optional fields are omitted entirely when blank.
//
// Tracking after submit reuses the EXISTING panels (Account -> My Bookings /
// Tutor Review already read the user's serviceRequests); nothing there changes.
import { track } from '../analytics/analytics.js';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { IconCheck } from '../components/icons.jsx';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';
import { PS_APPLICANT_FIELDS, PS_DISCLAIMER } from '../config/personalStatement.js';

const MAX_TEXT = 50000;   // mirrors the rules cap on payload.submissionText
const MAX_TOPIC = 1000;

const inputCls = 'w-full bg-book-bg border border-book-accent/30 px-3 py-2.5 text-book-text font-serif text-sm focus:outline-none focus:border-book-accent';
const labelCls = 'block font-display text-book-muted text-xs mb-1 font-semibold';

const emptyApplicant = () => PS_APPLICANT_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {});

export const PersonalStatementRequestPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { currentUser } = useAuth();

    // ?type=consultation opens the session form; anything else (incl. absent)
    // defaults to the draft review.
    const [mode, setMode] = useState(
        searchParams.get('type') === 'consultation' ? 'consultation' : 'review'
    );

    const [draft, setDraft] = useState('');
    const [preferredDate, setPreferredDate] = useState('');
    const [preferredTime, setPreferredTime] = useState('');
    const [topic, setTopic] = useState('');
    const [applicant, setApplicant] = useState(emptyApplicant);

    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null); // 'review' | 'consultation'
    const [error, setError] = useState(null);

    const setField = (key, value) => setApplicant((a) => ({ ...a, [key]: value }));

    // Only non-empty optional fields are written (keeps the payload small and
    // avoids storing blanks the admin queue would render as empty rows).
    const applicantPayload = () => {
        const out = {};
        for (const f of PS_APPLICANT_FIELDS) {
            const v = (applicant[f.key] || '').trim();
            if (v) out[f.key] = v.slice(0, f.max);
        }
        return out;
    };

    const submit = async () => {
        setError(null);
        if (AUTH_MODE === 'mock' || !db) {
            setError('Requests need the live app (emulator or firebase mode), not mock mode.');
            return;
        }
        const uid = currentUser?.uid;
        if (!uid) { setError('Please sign in to send a request.'); return; }

        if (mode === 'review') {
            if (!draft.trim()) { setError('Paste your personal statement draft to request a review.'); return; }
            if (draft.length > MAX_TEXT) {
                setError(`Your draft is too long (max ${MAX_TEXT.toLocaleString()} characters).`);
                return;
            }
        } else if (!preferredDate) {
            setError('Please choose a preferred date for your session.');
            return;
        }

        setSubmitting(true);
        try {
            const base = { serviceCategory: 'personal_statement', ...applicantPayload() };
            const requestDoc = mode === 'review'
                ? {
                    type: 'tutor_review', uid, status: 'requested',
                    payload: { submissionText: draft, ...base },
                    createdAt: serverTimestamp()
                }
                : {
                    type: 'booking', uid, status: 'requested',
                    payload: {
                        preferredDate,
                        preferredTime: preferredTime || '',
                        topic: (topic || '').trim().slice(0, MAX_TOPIC),
                        ...base
                    },
                    createdAt: serverTimestamp()
                };
            await addDoc(collection(db, 'serviceRequests'), requestDoc);
            track('generate_lead', { lead_type: 'personal_statement_' + mode });
            setSubmitted(mode);
        } catch (e) {
            setError((e && e.message) || 'Could not send your request. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // ---- confirmation ----
    if (submitted) {
        const wasReview = submitted === 'review';
        return (
            <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-book-bg paper-texture flex items-center justify-center px-4 py-12">
                <div className="max-w-lg w-full text-center border border-book-accent/30 bg-book-page/60 p-10 md:p-12 decorative-corner text-book-accent">
                    <div className="w-16 h-16 rounded-full border-2 border-book-accent flex items-center justify-center mx-auto mb-6">
                        <IconCheck className="w-8 h-8 text-book-accent" />
                    </div>
                    <h2 className="font-display text-3xl text-book-text mb-4">Request Sent</h2>
                    <p className="font-serif text-book-muted mb-8 leading-relaxed">
                        Danielle will read your {wasReview ? 'draft' : 'request'} and send you a fee quote.
                        You'll find it in your Account under {wasReview ? 'Tutor Review' : 'My Bookings'},
                        where you can accept and pay.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button onClick={() => navigate('/account')}
                            className="px-5 py-3 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors btn-sweep font-semibold">
                            Track it in my account
                        </button>
                        <button onClick={() => navigate('/personal-statement')}
                            className="px-5 py-3 border border-book-accent text-book-accent font-display text-xs hover:bg-book-accent/10 transition-colors font-semibold">
                            Back to Personal Statements
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ---- form ----
    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-book-bg paper-texture">
            <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16">
                <button onClick={() => navigate('/personal-statement')}
                    className="font-serif italic text-book-muted text-sm hover:text-book-accent transition-colors mb-6">
                    &larr; Back to Personal Statements
                </button>

                <div className="mb-8">
                    <div className="font-display text-book-accent text-xs mb-2 font-semibold">Personal Statements</div>
                    <h1 className="font-display text-3xl md:text-4xl text-book-text leading-tight mb-3">
                        {mode === 'review' ? 'Request a review' : 'Book a consultation'}
                    </h1>
                    <p className="font-serif italic text-book-muted">
                        Danielle reads every request personally and replies with a fee quote before any payment.
                    </p>
                </div>

                {/* Mode toggle */}
                <div className="flex gap-2 mb-8">
                    {[
                        { id: 'review', label: 'Draft review' },
                        { id: 'consultation', label: 'Consultation' }
                    ].map((m) => (
                        <button key={m.id} type="button" onClick={() => { setMode(m.id); setError(null); }}
                            className={`px-5 py-2.5 border font-display text-sm transition-colors font-semibold ${mode === m.id
                                ? 'border-book-accent bg-book-accent/10 text-book-accent'
                                : 'border-book-accent/30 text-book-muted hover:border-book-accent/60'}`}>
                            {m.label}
                        </button>
                    ))}
                </div>

                {/* Mode-specific fields */}
                <div className="border border-book-accent/20 bg-book-page/40 p-6 decorative-corner text-book-accent space-y-4 mb-6">
                    {mode === 'review' ? (
                        <div>
                            <label className={labelCls}>Your personal statement draft</label>
                            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={14} maxLength={MAX_TEXT}
                                placeholder="Paste your full draft here — however rough it is."
                                className={inputCls + ' resize-y leading-relaxed'} />
                            <p className="font-serif italic text-book-muted text-xs mt-1">
                                {draft.length.toLocaleString()} / {MAX_TEXT.toLocaleString()} characters
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelCls}>Preferred date</label>
                                    <input type="date" value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)}
                                        className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Preferred time</label>
                                    <input type="time" value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)}
                                        className={inputCls} />
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>What you'd like to focus on</label>
                                <textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={4} maxLength={MAX_TOPIC}
                                    placeholder="e.g. choosing between Medicine and Biomedical Science, and how to open my statement."
                                    className={inputCls + ' resize-y'} />
                            </div>
                        </>
                    )}
                </div>

                {/* Optional applicant info (both modes) */}
                <div className="border border-book-accent/20 bg-book-page/30 p-6 space-y-4 mb-6 text-book-accent">
                    <div>
                        <div className="font-display text-book-accent text-xs mb-1 font-semibold">
                            About your application <span className="text-book-muted">· optional</span>
                        </div>
                        <p className="font-serif italic text-book-muted text-xs">
                            Every field here is optional — it just helps Danielle pitch her feedback. Skip anything you'd rather not share.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {PS_APPLICANT_FIELDS.map((f) => (
                            <div key={f.key} className={f.key === 'targetUniversities' ? 'sm:col-span-2' : ''}>
                                <label className={labelCls}>{f.label}</label>
                                <input type="text" value={applicant[f.key]} maxLength={f.max}
                                    onChange={(e) => setField(f.key, e.target.value)}
                                    placeholder={f.placeholder} className={inputCls} />
                            </div>
                        ))}
                    </div>
                </div>

                {error && (
                    <p className="mb-4 font-serif text-red-300 text-sm border border-red-500/40 bg-red-500/10 p-3">{error}</p>
                )}

                <button onClick={submit} disabled={submitting}
                    className="px-5 py-3 bg-book-accent text-black font-display text-sm hover:bg-book-accentLight transition-colors btn-sweep disabled:opacity-40 disabled:cursor-not-allowed font-semibold">
                    {submitting ? 'Sending…' : mode === 'review' ? 'Send my draft' : 'Request this session'}
                </button>

                <p className="font-serif italic text-book-muted text-xs leading-relaxed mt-8 border-t border-book-accent/15 pt-5">
                    {PS_DISCLAIMER}
                </p>
            </div>
        </div>
    );
};

export default PersonalStatementRequestPage;
